import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auditLogAPI, adminAPI, authAPI } from '../services/api';
import { removeToken } from '../utils/auth';
import './OperationalLog.css';

function OperationalLog() {
    const navigate = useNavigate();
    const [logs, setLogs] = useState([]);
    const [users, setUsers] = useState([]);
    const [isAdmin, setIsAdmin] = useState(false);
    const [userName, setUserName] = useState('');
    const [userRole, setUserRole] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 50,
        total: 0,
        totalPages: 0
    });

    // Sorting
    const [sortConfig, setSortConfig] = useState({
        key: 'createdAt',
        direction: 'desc'
    });

    // Filters
    const [filters, setFilters] = useState({
        resourceType: '',
        actionType: '',
        userId: ''
    });

    useEffect(() => {
        loadUserInfo();
        loadLogs();
    }, [pagination.page, filters]);

    const loadUserInfo = async () => {
        try {
            const response = await authAPI.getCurrentUser();
            const user = response.data.data.user;
            setIsAdmin(user.role === 'ADMIN');
            setUserName(user.name);
            setUserRole(user.role);

            if (user.role === 'ADMIN') {
                // Load users for admin filter
                const usersResponse = await adminAPI.getUsers({});
                setUsers(usersResponse.data.data.users);
            }
        } catch (err) {
            console.error('Failed to load user info:', err);
        }
    };

    const handleLogout = () => {
        removeToken();
        navigate('/login');
    };

    const loadLogs = async () => {
        setLoading(true);
        setError('');
        try {
            const params = {
                page: pagination.page,
                limit: pagination.limit
            };

            if (filters.resourceType) params.resourceType = filters.resourceType;
            if (filters.actionType) params.actionType = filters.actionType;
            if (filters.userId) params.userId = filters.userId;

            const response = await auditLogAPI.getAuditLogs(params);
            setLogs(response.data.data.logs);
            setPagination(response.data.data.pagination);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load audit logs');
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
        setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page on filter change
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            setPagination(prev => ({ ...prev, page: newPage }));
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleString();
    };

    const getActionBadgeClass = (actionType) => {
        switch (actionType) {
            case 'CREATE': return 'badge badge-success';
            case 'UPDATE': return 'badge badge-warning';
            case 'DELETE': return 'badge badge-danger';
            default: return 'badge';
        }
    };

    const getResourceBadgeClass = (resourceType) => {
        switch (resourceType) {
            case 'SCANNER': return 'badge badge-scanner';
            case 'WEBHOOK': return 'badge badge-webhook';
            case 'SECURITY_POLICY': return 'badge badge-policy';
            default: return 'badge';
        }
    };

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const getSortedLogs = () => {
        const sorted = [...logs].sort((a, b) => {
            let aValue = a[sortConfig.key];
            let bValue = b[sortConfig.key];

            // Handle nested properties
            if (sortConfig.key === 'userName') {
                aValue = a.userName;
                bValue = b.userName;
            }

            if (aValue === null || aValue === undefined) return 1;
            if (bValue === null || bValue === undefined) return -1;

            if (typeof aValue === 'string') {
                aValue = aValue.toLowerCase();
                bValue = bValue.toLowerCase();
            }

            if (aValue < bValue) {
                return sortConfig.direction === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortConfig.direction === 'asc' ? 1 : -1;
            }
            return 0;
        });
        return sorted;
    };

    const getSortIcon = (columnKey) => {
        if (sortConfig.key !== columnKey) {
            return ' ↕';
        }
        return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
    };

    return (
        <div className="operational-log-page">
            <header className="dashboard-header">
                <div>
                    <h1>Scanner API Dashboard</h1>
                    {userName && (
                        <p className="user-info">
                            {userName} {userRole && <span className="badge">{userRole}</span>}
                        </p>
                    )}
                </div>
                <div className="header-actions">
                    <button onClick={handleLogout} className="logout-btn">
                        Logout
                    </button>
                </div>
            </header>

            <nav className="dashboard-nav">
                <button
                    onClick={() => navigate('/')}
                    className="nav-btn"
                >
                    ID Scanners
                </button>
                <button
                    onClick={() => navigate('/webhooks')}
                    className="nav-btn"
                >
                    My Webhooks
                </button>
                <button
                    onClick={() => navigate('/security-policies')}
                    className="nav-btn"
                >
                    Security Policies
                </button>
                <button
                    onClick={() => navigate('/operational-log')}
                    className="nav-btn active"
                >
                    Operational Log
                </button>
            </nav>

            <div className="page-content">
                <div className="page-header">
                    <h2>Operational Log</h2>
                    <p>Track all configuration changes to scanners, webhooks, and security policies</p>
                </div>

                {error && <div className="error">{error}</div>}

            <div className="filters">
                <div className="filter-group">
                    <label>Resource Type</label>
                    <select
                        value={filters.resourceType}
                        onChange={(e) => handleFilterChange('resourceType', e.target.value)}
                    >
                        <option value="">All Resources</option>
                        <option value="SCANNER">Scanner</option>
                        <option value="WEBHOOK">Webhook</option>
                        <option value="SECURITY_POLICY">Security Policy</option>
                    </select>
                </div>

                <div className="filter-group">
                    <label>Action Type</label>
                    <select
                        value={filters.actionType}
                        onChange={(e) => handleFilterChange('actionType', e.target.value)}
                    >
                        <option value="">All Actions</option>
                        <option value="CREATE">Create</option>
                        <option value="UPDATE">Update</option>
                        <option value="DELETE">Delete</option>
                    </select>
                </div>

                {isAdmin && (
                    <div className="filter-group">
                        <label>User</label>
                        <select
                            value={filters.userId}
                            onChange={(e) => handleFilterChange('userId', e.target.value)}
                        >
                            <option value="">All Users</option>
                            {users.map(user => (
                                <option key={user.id} value={user.id}>
                                    {user.name} ({user.email})
                                </option>
                            ))}
                        </select>
                    </div>
                )}
            </div>

            <div className="logs-table-container">
                {loading ? (
                    <div className="loading">Loading audit logs...</div>
                ) : logs.length === 0 ? (
                    <div className="no-data">No audit logs found</div>
                ) : (
                    <table className="logs-table">
                        <thead>
                            <tr>
                                <th onClick={() => handleSort('createdAt')} className="sortable">
                                    Timestamp{getSortIcon('createdAt')}
                                </th>
                                <th onClick={() => handleSort('userName')} className="sortable">
                                    User{getSortIcon('userName')}
                                </th>
                                <th onClick={() => handleSort('actionType')} className="sortable">
                                    Action{getSortIcon('actionType')}
                                </th>
                                <th onClick={() => handleSort('resourceType')} className="sortable">
                                    Resource{getSortIcon('resourceType')}
                                </th>
                                <th onClick={() => handleSort('resourceName')} className="sortable">
                                    Resource Name{getSortIcon('resourceName')}
                                </th>
                                <th>Changes</th>
                            </tr>
                        </thead>
                        <tbody>
                            {getSortedLogs().map(log => (
                                <tr key={log.id}>
                                    <td>{formatDate(log.createdAt)}</td>
                                    <td>
                                        <div className="user-info">
                                            <div className="user-name">{log.userName}</div>
                                            <div className="user-email">{log.userEmail}</div>
                                        </div>
                                    </td>
                                    <td>
                                        <span className={getActionBadgeClass(log.actionType)}>
                                            {log.actionType}
                                        </span>
                                    </td>
                                    <td>
                                        <span className={getResourceBadgeClass(log.resourceType)}>
                                            {log.resourceType.replace('_', ' ')}
                                        </span>
                                    </td>
                                    <td>{log.resourceName || `ID: ${log.resourceId}`}</td>
                                    <td className="changes-summary">
                                        {log.changesSummary ? log.changesSummary.replace(/; /g, '\n') : ''}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {pagination.totalPages > 1 && (
                <div className="pagination">
                    <button
                        onClick={() => handlePageChange(pagination.page - 1)}
                        disabled={pagination.page === 1}
                    >
                        Previous
                    </button>
                    <span className="page-info">
                        Page {pagination.page} of {pagination.totalPages} ({pagination.total} total records)
                    </span>
                    <button
                        onClick={() => handlePageChange(pagination.page + 1)}
                        disabled={pagination.page === pagination.totalPages}
                    >
                        Next
                    </button>
                </div>
            )}
            </div>
        </div>
    );
}

export default OperationalLog;
